import { useEffect, useRef } from 'react';
import { useIntersection } from '@mantine/hooks';
import TagManager from 'react-gtm-module';

/** Fraction of the element that must be on screen to count as visible. */
export const MIN_VISIBLE_RATIO = 1;

/**
 * Triggers an analytics event the first time the target element is fully visible.
 *
 * @param event - GTM event name to trigger.
 * @param contentKey - Identifier for the span or network group displayed.
 */
export const useViewedOnceAnalytics = <T extends HTMLElement>(
  event: string,
  contentKey: string | number | null | undefined
) => {
  const { ref, entry } = useIntersection<T>({ threshold: MIN_VISIBLE_RATIO });
  const isViewed = useRef(false);
  const lastContentKey = useRef(contentKey);

  useEffect(() => {
    if (lastContentKey.current !== contentKey) {
      lastContentKey.current = contentKey;
      isViewed.current = false;
    }
    const isVisible = (entry?.intersectionRatio ?? 0) >= MIN_VISIBLE_RATIO;
    if (isVisible && !isViewed.current) {
      isViewed.current = true;
      TagManager.dataLayer({ dataLayer: { event } });
    }
  }, [entry?.intersectionRatio, event, contentKey]);

  return ref;
};
